import React, { useMemo } from 'react';

import { Chip, Stack, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';

import { VoucherLogWarning } from '~/services/PromoService';

import { humanize } from '~/utils/humanize';
import {
  type VoucherLogWarningSummary,
  getVoucherLogWarningCountsByLevel,
  normalizeVoucherLogWarningLevel,
} from '~/utils/voucherLogWarnings';

type Props = {
  warningSummaries: Array<VoucherLogWarningSummary>;
};

function getWarningLevelChipColor(level: string): ChipProps['color'] {
  switch (normalizeVoucherLogWarningLevel(level)) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'success':
      return 'success';
    default:
      return 'default';
  }
}

function getWarningLevelLabel(level: string): string {
  return humanize(normalizeVoucherLogWarningLevel(level));
}

export default function VoucherLogWarningsSummaryButton({ warningSummaries }: Props) {
  const warnings = useMemo<Array<VoucherLogWarning>>(
    () => warningSummaries.map(({ warning }) => warning),
    [warningSummaries],
  );
  const levelCounts = useMemo(() => getVoucherLogWarningCountsByLevel(warnings), [warnings]);

  return (
    <DebugModal
      type="generic"
      hideIcon
      title={`Voucher Log Warnings (${warningSummaries.length})`}
      data={warningSummaries}
      buttonProps={{
        sx: {
          textTransform: 'none',
          minWidth: 0,
          px: 1,
          py: 0.5,
        },
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color={warningSummaries.length > 0 ? 'text.primary' : 'text.secondary'}>
          Warnings
        </Typography>
        {levelCounts.length > 0 ? (
          levelCounts.map(({ level, count }) => (
            <Chip
              key={level}
              size="small"
              variant="outlined"
              color={getWarningLevelChipColor(level)}
              label={`${getWarningLevelLabel(level)}: ${count}`}
            />
          ))
        ) : (
          <Chip size="small" variant="outlined" label="0" />
        )}
      </Stack>
    </DebugModal>
  );
}
